import { OperationVariables } from "@apollo/client";
import { ArrowDown, ArrowUp, CheckIcon } from "lucide-react-native";
import { useCallback } from "react";
import { Checkbox, CheckboxIcon, CheckboxIndicator } from "../../ui/checkbox";
import { Icon } from "../../ui/icon";
import { Pressable } from "../../ui/pressable";
import { TableHead, TableHeader, TableRow } from "../../ui/table";
import { Text } from "../../ui/text";
import { alignToJustify, alignToTextAlign } from "./helpers";
import { Column, DataGridProps, SortDirection, SortObject } from "./types";

export default function DataGridHeader<
  T extends object,
  TData,
  TVariables extends OperationVariables,
  F extends object,
  D extends object,
>(props: DataGridProps<T, TData, TVariables, F, D>) {
  const { columns, sortOptions, variant, size } = props;
  const isColumnSortable = useCallback(
    (column: Column<T, F, D>) => {
      return sortOptions && column.sortable;
    },
    [sortOptions],
  );
  const handleSort = useCallback(
    (column: Column<T, F, D>) => {
      if (!isColumnSortable(column) || !column.sortable || !sortOptions) return;
      const sortValues = column.sortValue.split(",");
      const previousSort = sortOptions.sort.find(
        (s) => s.key === sortValues[0],
      )?.direction;
      switch (previousSort) {
        case "asc":
          sortOptions.onSort((prevSort) => [
            ...sortValues.map((s) => ({
              key: s,
              direction: "desc" as SortDirection,
            })),
            ...prevSort.filter((p) => !sortValues.includes(p.key)),
          ]);
          break;
        case "desc":
          sortOptions.onSort((prev) =>
            prev.filter((p) => !sortValues.includes(p.key)),
          );
          break;
        default:
          sortOptions.onSort((prevSort) => [
            ...sortValues.map((s) => ({
              key: s,
              direction: "asc" as SortDirection,
            })),
            ...prevSort.filter((p) => !sortValues.includes(p.key)),
          ]);
          break;
      }
    },
    [sortOptions, isColumnSortable],
  );
  const renderSortIcon = (
    column: Column<T, F, D>,
    sortKey?: SortObject | null,
  ) => {
    if (!column.sortable) return null;
    return (
      <Icon
        as={sortKey?.direction === "desc" ? ArrowDown : ArrowUp}
        action="faint"
        size={"sm"}
        className="hidden group-hover/table-head:block group-aria-selected/table-head:block group-aria-selected/table-head:text-typography-500"
      />
    );
  };
  const renderColumnHeader = (column: Column<T, F, D>) => {
    const justify = alignToJustify(column.align);
    const textAlign = alignToTextAlign(column.align);
    const sortValues = column.sortable && column.sortValue.split(",");
    const thisHeaderSortKey = sortValues
      ? sortOptions?.sort.find((s) => s.key === sortValues[0])
      : null;
    return (
      <TableHead
        key={String(column.id)}
        style={{ width: column.width }}
        size={size}
      >
        <Pressable
          onPress={
            isColumnSortable(column) ? () => handleSort(column) : undefined
          }
          disablePointer={!isColumnSortable(column)}
          className={`group/table-head flex flex-row gap-x-2 items-center ${justify}`}
          aria-selected={!!thisHeaderSortKey}
        >
          <Text
            size={"sm"}
            className={`font-medium flex-1 shrink ${textAlign}`}
            numberOfLines={1}
            isTruncated
            {...props.headerTextProps}
          >
            {column.header}
          </Text>
          {renderSortIcon(column, thisHeaderSortKey)}
        </Pressable>
      </TableHead>
    );
  };
  return (
    <TableHeader>
      <TableRow>
        {props.checkbox && (
          <TableHead style={{ width: 40 }} size={size}>
            {!!props.selectAll && (
              <Checkbox
                size="sm"
                value={"all"}
                action="brand"
                isChecked={
                  props.selectionModel.length === props.data?.length &&
                  props.selectionModel.length > 0
                }
                onChange={(isSelected) => {
                  isSelected &&
                    props.data &&
                    props.onSelectionChange?.([
                      ...props.data.map((item) => props.getId(item)),
                    ]);
                  !isSelected && props.data && props.onSelectionChange?.([]);
                }}
              >
                <CheckboxIndicator>
                  <CheckboxIcon as={CheckIcon} />
                </CheckboxIndicator>
              </Checkbox>
            )}
          </TableHead>
        )}
        {columns.map(renderColumnHeader)}
      </TableRow>
    </TableHeader>
  );
}
